import {FlowStateController} from "@nolimitcity/slot-game/bin/flowcontrol/FlowStateController";
import {FlowStatePresentation} from "@nolimitcity/slot-game/bin/flowcontrol/FlowStatePresentation";
import {DasUBoot} from "../DasUBoot";
import {gsap} from "gsap";
import {DasUBootSymbol, DasUBootSymbolStates} from "../reels/DasUBootSymbol";
import {DasUBootSound} from "../sound/DasUBootSound";
import {isBombSymbol} from "../reels/symbols/BombSymbol";
import {SimpleReelModelSymbolData} from "../../core/reel/simplereel/SimpleReelModel";
import {DasUBootStatics, VignetteTint} from "../utils/DasUBootStatics";
import {
    SimplePresentation,
    SimplePresentationRunner,
    SimplePresentationRunnerController
} from "../../core/winpresentation/SimplePresentationRunner";
import {AvalancheXWays} from "../runnerpresentations/avalancheXWays";
import {LocalPresentation} from "../runnerpresentations/LocalPresentation";
import {ReelUtils} from "../utils/ReelUtils";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import {MathUtils} from "../utils/MathUtils";
import {DasUBootRoundType} from "../server/DasUBootData";
import {TorpedoSymbol} from "../reels/symbols/TorpedoSymbol";
import {GSAPUtils} from "../utils/GSAPUtils";
import {ArrayUtils} from "../utils/ArrayUtils";

/**
 * Created by Jerker Nord on 2021-01-28.
 */

export class DasUBootAvalancheResultPresentation implements FlowStatePresentation {
    private _presentations:SimplePresentation[];

    constructor() {
        this._presentations = [
            new LocalPresentation((controller:SimplePresentationRunnerController) => this.avalancheDrop(controller), () => true),
            new AvalancheXWays(),
        ]
    }


    public start(flowStateController:FlowStateController) {
        SimplePresentationRunner.run(this._presentations, () => flowStateController.finish(), (callback?:Function) => flowStateController.setSkipCallback(callback));
    }


    public avalancheDrop(controller:SimplePresentationRunnerController) {

        for (let i:number = 0; i < DasUBoot.reelView.reels.length; i++) {
            DasUBoot.reelView.reels[i].replaceAllPaddingSymbolsBefore(DasUBoot.model.clientData.reels[i], true);
        }

        const tl:gsap.core.Timeline = gsap.timeline({onComplete:() => controller.finish()});

        const allAvalancheSymbols:SimpleReelModelSymbolData[][] = [];
        const stackedWilds = [];
        const torpedoSymbols = [];

        for (let i:number = 0; i < DasUBoot.model.avalancheDropInReels.length; i++) {
            allAvalancheSymbols.push([]);
            for (let j:number = DasUBoot.model.avalancheDropInReels[i].length - 1; j >= 0; j--) {
                const symbolData = DasUBoot.reelView.reels[i].addAvalancheSymbol(j, DasUBoot.model.avalancheDropInReels[i][j]);
                allAvalancheSymbols[i].push(symbolData);

                if (symbolData.name == "W4") {
                    stackedWilds.push(symbolData);
                }
                if (symbolData.name == "T") {
                    torpedoSymbols.push(symbolData);
                }
            }
        }

        for (let sWild of stackedWilds) {
            (<DasUBootSymbol>sWild.positionable).moveToNormalLayer();
        }

        for (let sTorpedo of torpedoSymbols) {
            for (let data of DasUBoot.model.clientData.torpedoParts) {
                if (data && data.reel == sTorpedo.reelId) {
                    let symbol = <TorpedoSymbol>sTorpedo.positionable;
                    symbol.addTorpedoPart();
                }
            }
        }


        const xBombReelScale:number[] = [1,1,1,1,1,1];

        for (let data of DasUBoot.model.clientData.xWays){
            const symData = DasUBoot.reelView.getVisibleSymbolAt(data.reel,data.position);
            const symbol = <DasUBootSymbol>symData.positionable;
            xBombReelScale[data.reel] = symbol.getAvalancheScale();
        }


        let stopDelay = DasUBoot.apiPlugin.settings.get(APISetting.FAST_SPIN, false) ? DasUBootStatics.reelStopDelayFastSpin : DasUBootStatics.reelStopDelay;
        stopDelay *= 0.3; //Make it a bit snappier in avalanche
        let stopTime = 0;
        const reelStopAnimations = gsap.timeline();

        for (let i:number = 0; i < allAvalancheSymbols.length; i++) {
            let reelsScale = (DasUBoot.model.data.roundType == DasUBootRoundType.FREE_SPIN && i == 2) ? 0.75 : 1;


            //const isNearWin = DasUBoot.model.gameData.nearWinReels[i];
            const nearWin = this.getNearWin(i);

            //Symbols falling
            const symbolFallAnims = [];
            for (let j:number = 0; j < allAvalancheSymbols[i].length; j++) {
                const symbolData = allAvalancheSymbols[i][j];
                const symbol = <DasUBootSymbol>symbolData.positionable;
                symbol.position.y = (symbolData.reelPosition - 4) * DasUBootStatics.symbolHeight;

                const symbolTL:gsap.core.Timeline = gsap.timeline();
                if (xBombReelScale[i] != 1){
                    symbol.resize(xBombReelScale[i], 0,0.001);
                }
                symbolTL.add(symbol.getStateAnimation(DasUBootSymbolStates.DROP_IN));
                symbolTL.add(gsap.to(symbol.position, {
                    duration:0.4,
                    y:symbolData.reelPosition * DasUBootStatics.symbolHeight * reelsScale * xBombReelScale[i],
                    ease:DasUBootStatics.avalancheBounceEase
                }));
                symbolTL.add(() => {symbol.getStateAnimation(DasUBootSymbolStates.LANDING)}, "<0.16");

                if (!isBombSymbol(symbol)) {
                    symbolTL.add(() => DasUBoot.soundPlayer.playSound(DasUBootSound.avalancheLandingSounds.next()), "<0.16");
                }


                symbolFallAnims.push(symbolTL);
            }


            let reelTl:gsap.core.Timeline = gsap.timeline();
            if (nearWin != undefined) {
                let symbolsDur = 0;
                for (let anim of symbolFallAnims) {
                    symbolsDur += anim.duration();
                }
                let remainder = nearWin.duration() - symbolsDur;
                if (remainder > 0) {
                    remainder = remainder / symbolFallAnims.length;
                } else {
                    remainder = remainder / (symbolFallAnims.length - 1);
                }

                reelTl = GSAPUtils.createTimelineFromArray(symbolFallAnims, "+=" + remainder);
            } else {
                reelTl = GSAPUtils.createTimelineFromArray(symbolFallAnims, "<0.03");
            }


            let nextReelDelay = stopDelay;
            let addedNewSym = allAvalancheSymbols[i].length > 0;
            let isNextNearWin = false;
            if (addedNewSym) {
                if (nearWin) {
                    reelTl.add(nearWin, 0)
                }
                reelTl.add(() => ReelUtils.highlightWinLine(i, 2), "-=0.2");
                reelStopAnimations.add(reelTl, stopTime);
            } else {
                if (nearWin) {
                    nearWin.kill();
                }
            }


            if (i == 4) {
                reelStopAnimations.add(() => {
                    DasUBoot.soundPlayer.stopNearWin(DasUBoot.model.data.nextMode);
                    DasUBoot.backgroundElements.stopNearWin(DasUBoot.model.data.nextMode);
                    DasUBoot.reelView.reelFrame.stopScatterAttention();
                });
            }

            if (i >= 2 && DasUBoot.model.possibleWinData.combinedReels[i] && addedNewSym && !isNextNearWin) {
                //Delays next stop if there is a possible win, slightly more for each reel
                const delayMultiplier = MathUtils.map(i, 2, 5, 6, 12);
                nextReelDelay = stopDelay * delayMultiplier;
            }

            if (nearWin) {
                nextReelDelay = reelTl.duration();
            }


            stopTime += nextReelDelay;


        }

        if (reelStopAnimations.duration() == 0) {
            tl.add(gsap.to(this, {duration:0.6}));
        }

        tl.add(reelStopAnimations);
        tl.add(DasUBoot.reelView.reelFrame.avalancheAttention.hide(0.5), ">-0.5");

        if (ArrayUtils.isAllFalse(DasUBoot.model.clientData.xBomb)) {
            tl.add(() => DasUBoot.backgroundElements.stopXBombAttention());
        }
    }


    private getNearWin(reelId:number):gsap.core.Timeline | undefined {

        //Normal near win
        if (DasUBoot.model.gameData.nearWinReels[reelId]) {
            let tl:gsap.core.Timeline = gsap.timeline();
            tl.add(DasUBoot.reelView.reels[reelId].getShowNearWin());
            tl.add(() => DasUBoot.backgroundElements.startNearWin(VignetteTint.GREEN), 0);
            tl.add(() => DasUBoot.soundPlayer.startNearWin(), 0.0);
            tl.add(DasUBoot.reelView.reels[reelId].getHideNearWin());
            return tl;
        }

        return undefined;
    }

}